import { TaskParty, TaskStatus } from '@prisma/client';

/**
 * Centralized task label configuration.
 * This is the single source of truth for service request task labels across the app.
 * Both the homepage and service request views should use these labels.
 */

/**
 * The subset of task statuses that are used in incorporation service request UIs.
 * CANCELED is not used in these flows.
 *
 * Uses Extract to maintain connection to TaskStatus -
 * if those values change in Prisma, TypeScript will error here.
 */
type IncorporationTaskStatus = Extract<
  TaskStatus,
  'TO_DO' | 'IN_PROGRESS' | 'COMPLETED' | 'BACKLOG'
>;

type TaskLabelsByStatus = Record<IncorporationTaskStatus, string>;
type TaskLabelsByRole = Record<
  Exclude<TaskParty, 'ADMIN' | 'SYSTEM'>,
  TaskLabelsByStatus
>;
type TaskLabelsByTaskId = Record<string, TaskLabelsByRole>;
type TaskLabelConfig = Partial<Record<string, TaskLabelsByTaskId>>;

/**
 * Task labels organized by: Commenda service -> TaskId -> Role -> Status
 *
 * Every status must have a label defined to ensure consistent UI behavior.
 */
export const TaskLabels: TaskLabelConfig = {
  // Canada Corporation Incorporation
  EM_INC_CA: {
    FILL_CORPORATION_DETAILS: {
      [TaskParty.CLIENT]: {
        [TaskStatus.TO_DO]:
          'Complete entity formation questionnaire',
        [TaskStatus.IN_PROGRESS]: 'Submit the form details',
        [TaskStatus.COMPLETED]:
          'Entity formation questionnaire completed',
        [TaskStatus.BACKLOG]:
          'Complete entity formation questionnaire',
      },
      [TaskParty.AGENT]: {
        [TaskStatus.TO_DO]:
          'Entity formation questionnaire completion',
        [TaskStatus.IN_PROGRESS]:
          'Entity formation questionnaire is in progress',
        [TaskStatus.COMPLETED]:
          'Please complete your entity formation questionnaire',
        [TaskStatus.BACKLOG]:
          'Entity formation questionnaire completion',
      },
    },
    REVIEW_CORPORATION_DETAILS: {
      [TaskParty.CLIENT]: {
        [TaskStatus.TO_DO]:
          'Commenda approves entity formation questionnaire',
        [TaskStatus.IN_PROGRESS]:
          'Commenda reviewing entity formation questionnaire',
        [TaskStatus.COMPLETED]:
          'Entity formation questionnaire approved',
        [TaskStatus.BACKLOG]:
          'Commenda approves entity formation questionnaire',
      },
      [TaskParty.AGENT]: {
        [TaskStatus.TO_DO]:
          'Approve entity formation questionnaire',
        [TaskStatus.IN_PROGRESS]:
          'Reviewing entity formation questionnaire',
        [TaskStatus.COMPLETED]:
          'Entity formation questionnaire approved',
        [TaskStatus.BACKLOG]:
          'Approve entity formation questionnaire',
      },
    },
  },

  // US C Corp Incorporation
  EM_INC_US_CCORP: {
    FILL_CORPORATION_DETAILS: {
      [TaskParty.CLIENT]: {
        [TaskStatus.TO_DO]:
          'Complete entity formation questionnaire',
        [TaskStatus.IN_PROGRESS]: 'Submit the form details',
        [TaskStatus.COMPLETED]:
          'Entity formation questionnaire completed',
        [TaskStatus.BACKLOG]:
          'Complete entity formation questionnaire',
      },
      [TaskParty.AGENT]: {
        [TaskStatus.TO_DO]:
          'Entity formation questionnaire completion',
        [TaskStatus.IN_PROGRESS]:
          'Entity formation questionnaire is in progress',
        [TaskStatus.COMPLETED]:
          'Entity formation questionnaire completed',
        [TaskStatus.BACKLOG]:
          'Entity formation questionnaire completion',
      },
    },
    REVIEW_CORPORATION_DETAILS: {
      [TaskParty.CLIENT]: {
        [TaskStatus.TO_DO]:
          'Commenda approves entity formation questionnaire',
        [TaskStatus.IN_PROGRESS]:
          'Commenda reviewing entity formation questionnaire',
        [TaskStatus.COMPLETED]:
          'Entity formation questionnaire approved',
        [TaskStatus.BACKLOG]:
          'Commenda approves entity formation questionnaire',
      },
      [TaskParty.AGENT]: {
        [TaskStatus.TO_DO]:
          'Approve entity formation questionnaire',
        [TaskStatus.IN_PROGRESS]:
          'Reviewing entity formation questionnaire',
        [TaskStatus.COMPLETED]:
          'Entity formation questionnaire approved',
        [TaskStatus.BACKLOG]:
          'Approve entity formation questionnaire',
      },
    },
  },

  // US LLC Incorporation - Uses "LLC" terminology
  EM_INC_US_LLC: {
    FILL_CORPORATION_DETAILS: {
      [TaskParty.CLIENT]: {
        [TaskStatus.TO_DO]:
          'Complete LLC formation questionnaire',
        [TaskStatus.IN_PROGRESS]: 'Submit the form details',
        [TaskStatus.COMPLETED]:
          'LLC formation questionnaire completed',
        [TaskStatus.BACKLOG]:
          'Complete LLC formation questionnaire',
      },
      [TaskParty.AGENT]: {
        [TaskStatus.TO_DO]:
          'LLC formation questionnaire completion',
        [TaskStatus.IN_PROGRESS]:
          'LLC formation questionnaire is in progress',
        [TaskStatus.COMPLETED]:
          'LLC formation questionnaire completed',
        [TaskStatus.BACKLOG]:
          'LLC formation questionnaire completion',
      },
    },
    REVIEW_CORPORATION_DETAILS: {
      [TaskParty.CLIENT]: {
        [TaskStatus.TO_DO]:
          'Commenda approves LLC formation questionnaire',
        [TaskStatus.IN_PROGRESS]:
          'Commenda reviewing LLC formation questionnaire',
        [TaskStatus.COMPLETED]:
          'LLC formation questionnaire approved',
        [TaskStatus.BACKLOG]:
          'Commenda approves LLC formation questionnaire',
      },
      [TaskParty.AGENT]: {
        [TaskStatus.TO_DO]:
          'Approve LLC formation questionnaire',
        [TaskStatus.IN_PROGRESS]:
          'Reviewing LLC formation questionnaire',
        [TaskStatus.COMPLETED]:
          'LLC formation questionnaire approved',
        [TaskStatus.BACKLOG]:
          'Approve LLC formation questionnaire',
      },
    },
  },
};

/**
 * Default task labels used as fallback when a service type isn't explicitly configured.
 * This covers incorporation types like UAE, UK, IE, SG and India, whose labels match these.
 */
const DefaultTaskLabels: TaskLabelsByTaskId = {
  FILL_CORPORATION_DETAILS: {
    [TaskParty.CLIENT]: {
      [TaskStatus.TO_DO]:
        'Complete entity formation questionnaire',
      [TaskStatus.IN_PROGRESS]: 'Submit the form details',
      [TaskStatus.COMPLETED]:
        'Entity formation questionnaire completed',
      [TaskStatus.BACKLOG]:
        'Complete entity formation questionnaire',
    },
    [TaskParty.AGENT]: {
      [TaskStatus.TO_DO]:
        'Entity formation questionnaire completion',
      [TaskStatus.IN_PROGRESS]:
        'Entity formation questionnaire is in progress',
      [TaskStatus.COMPLETED]:
        'Entity formation questionnaire completed',
      [TaskStatus.BACKLOG]:
        'Entity formation questionnaire completion',
    },
  },
  REVIEW_CORPORATION_DETAILS: {
    [TaskParty.CLIENT]: {
      [TaskStatus.TO_DO]:
        'Agent approves entity formation questionnaire',
      [TaskStatus.IN_PROGRESS]:
        'Agent reviewing entity formation questionnaire',
      [TaskStatus.COMPLETED]:
        'Entity formation questionnaire approved',
      [TaskStatus.BACKLOG]:
        'Agent approves entity formation questionnaire',
    },
    [TaskParty.AGENT]: {
      [TaskStatus.TO_DO]:
        'Approve entity formation questionnaire',
      [TaskStatus.IN_PROGRESS]:
        'Reviewing entity formation questionnaire',
      [TaskStatus.COMPLETED]:
        'Entity formation questionnaire approved',
      [TaskStatus.BACKLOG]:
        'Approve entity formation questionnaire',
    },
  },
};

/**
 * Get a task label from the centralized configuration.
 *
 * @param serviceId - The request's Commenda service (e.g., EM_INC_CA)
 * @param taskId - The task identifier (e.g., FILL_CORPORATION_DETAILS)
 * @param role - The user's role (CLIENT or AGENT)
 * @param status - The task status
 * @param fallback - Optional fallback label if not found in config
 * @returns The label string, or the fallback if not found
 */
export function getTaskLabel(
  serviceId: string | undefined,
  taskId: string,
  role: TaskParty,
  status: TaskStatus,
  fallback?: string
): string | undefined {
  // ADMIN (and SYSTEM) roles use the same labels as AGENT
  const effectiveRole =
    role === TaskParty.CLIENT ? TaskParty.CLIENT : TaskParty.AGENT;

  const serviceLabels = serviceId ? TaskLabels[serviceId] : undefined;

  // Try service-specific labels first, then fall back to defaults
  const label =
    serviceLabels?.[taskId]?.[effectiveRole]?.[
      status as IncorporationTaskStatus
    ] ??
    DefaultTaskLabels[taskId]?.[effectiveRole]?.[
      status as IncorporationTaskStatus
    ];

  return label ?? fallback;
}
